// angular imports
import { Component, Input, Output, EventEmitter, OnInit, SimpleChanges, OnChanges, ViewChild } from '@angular/core';

// devextreme imports
import { DxDataGridComponent, DxFileUploaderComponent, DxPopupComponent } from 'devextreme-angular';

// cc-common imports
import { DxWidgetUtils, IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// service imports
import { HelperService } from '../../../../../_services/shared-services/helper.service';
import { FileReaderService } from '../../../../../_services/shared-services/file-reader.service';
import { UserService } from '../../../../../_services/data-services/user.service';

// component imports
import { PropertyCommunityDocumentsComponent } from '../../property-community-documents/property-community-documents.component';

// model imports
import { DocumentTreeResultDto } from '../../../../../shared-document-module/_models/document-tree-result-dto';

// constants imports
import { ActionConstants } from '../../../../../_constants/action-constants';
import { EmailConstants } from '../../../../../_constants/email-constants';

@Component({
    selector: 'app-attachment-uploader',
    templateUrl: './attachment-uploader.component.html',
    styleUrls: ['./attachment-uploader.component.scss']
})
export class AttachmentUploaderComponent implements OnInit, OnChanges {
    @ViewChild('attachmentsPopup', { static: false }) attachmentsPopup: DxPopupComponent;
    @ViewChild('attachmentGrid', { static: false }) attachmentGrid: DxDataGridComponent;
    @ViewChild('fileUploader', { static: false }) fileUploader: DxFileUploaderComponent;
    @ViewChild('propertyCommunityDocuments', { static: false })
    propertyCommunityDocuments: PropertyCommunityDocumentsComponent;
    @ViewChild('messageBox', { static: false }) messageBox: MessageBoxComponent;

    @Input() visible = false;
    @Input() selectedAttachments: any[];
    @Input() communityId: number;
    @Input() customerId: string;
    @Input() communityApiUrl: string = null;
    @Input() communityDocuments: Array<DocumentTreeResultDto> = [];
    @Input() ownerApiUrl: string = null;
    @Input() ownerDocuments: Array<DocumentTreeResultDto> = [];
    @Input() allowDelete = true;
    @Input() isViewOnly = false;
    @Input() title = 'Attachments';
    @Input() multipleSelect = true;
    @Input() acceptedTypes = ['*'];
    @Input() allowedFileExtensions = [];

    @Output() onAttachmentUpdate = new EventEmitter<any>();
    @Output() onDocumentDelete = new EventEmitter<any>();
    @Output() onClose = new EventEmitter<any>();

    isInternalUser: boolean;
    loadingVisible = false;

    iconClose = IconConstants.CLOSE;
    iconRemove = IconConstants.DELETE;
    iconUpload = IconConstants.UPLOAD;
    iconPlus = IconConstants.ADD;

    attachLocal = EmailConstants.ATTACH_LOCAL_FILE;
    attachCommunity = EmailConstants.ATTACH_COMMUNITY_DOC;
    attachOwner = EmailConstants.ATTACH_OWNER_DOC;

    constructor(
        private readonly helperService: HelperService,
        private readonly fileReaderService: FileReaderService,
        private readonly userService: UserService
    ) {}

    ngOnInit() {
        this.isInternalUser = this.userService.isInternal();
        this.visible = true;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            Utils.notNullOrEmpty(changes.selectedAttachments) &&
            Utils.notNullOrEmpty(changes.selectedAttachments.currentValue)
        ) {
            const selectedAttachments = changes.selectedAttachments.currentValue;
            this.processAttachments(selectedAttachments);
        }
    }

    get acceptedFileTypes(): string {
        return this.acceptedTypes.join(',');
    }

    get showAttachmentButton(): boolean {
        /**
         * Logic:
         * If file upload mode is multiple file, dont hide the attachment button
         * If file upload mode is single file, then once file is selected, hide the attachment button
         */

        if (Utils.isTrue(this.multipleSelect)) {
            return true;
        } else if (Utils.isFalse(this.multipleSelect)) {
            return Utils.equals(this.selectedAttachments.length, 0);
        }

        return true;
    }

    onAttachmentPopupShown(): void {
        this.attachmentsPopup.instance.repaint();
    }

    onAttachmentPopupHidden(): void {
        this.closeViewer();
    }

    hasDownloadUrl(data: any): boolean {
        return Utils.notNullOrEmpty(data.fileUrl) || Utils.notNullOrEmpty(data.downloadUrl);
    }

    hasFileId(data: any): boolean {
        return (
            Utils.notNullAndDefined(data.fileId) ||
            Utils.notNullAndDefined(data.fileStoreId) ||
            Utils.notNullAndDefined(data.fileStoreID) ||
            Utils.notNullAndDefined(data.attachmentId)
        );
    }

    onRowPrepared(event): void {
        if (Utils.equals(event.rowType, 'data')) {
            if (Utils.equalsIgnoreCase(event.data.action, ActionConstants.ADD)) {
                event.rowElement.className += ' cc-highlight-new-attachment-row';
            }
        }
    }

    onContentReady(event): void {
        event.component.filter(['action', '<>', ActionConstants.DELETE]);
    }

    onCloseClick(): void {
        this.closeViewer();
    }

    onDownloadFile(file: any): void {
        if (Utils.isTrue(this.hasFileId(file))) {
            this.downloadFile(file);
        } else if (file instanceof File) {
            const blob: Blob = new Blob([file]);
            this.helperService.downloadFileUsingBlob(blob, file.name);
        }
    }

    onRemoveAttachment(record: any): void {
        if (Utils.isTrue(this.multipleSelect)) {
            // If file upload mode is multiple file
            this.removeAttachment(record);
        } else {
            // If file upload mode is single file, then get the confirmation before delete
            const dialog = this.helperService.getConfirmationDialog(
                'Confirmation',
                'Are you sure you want to remove this file?',
                'Yes',
                'No',
                'danger'
            );

            dialog.show().then((answer: boolean) => {
                if (Utils.isTrue(answer)) {
                    this.removeAttachment(record);
                }
            });
        }
    }

    onAttachmentsSelect(event): void {
        const localAttachments = event.value;

        localAttachments.forEach((attachment) => {
            if (Utils.isFalse(this.isAttachmentExists(attachment))) {
                attachment.action = ActionConstants.ADD;
                this.fileReaderService.readFileToBase64(attachment).subscribe((base64Data) => {
                    attachment.fileBase64String = base64Data;
                    attachment['allowDelete'] = this.allowDelete;
                    if (Utils.isTrue(this.multipleSelect)) {
                        this.selectedAttachments.push(attachment);
                    } else {
                        this.selectedAttachments = [attachment];
                    }
                    this.updateAttachments(this.selectedAttachments);
                });
            } else {
                this.messageBox.showErrorMessage('Error', 'The selected file already exist please select another file');
            }
            this.fileUploader.instance.reset();
        });
    }

    onLocalAttachment(): void {
        const uploader: any = this.fileUploader.instance;
        uploader._isCustomClickEvent = true;
        uploader._$fileInput[0].click();
    }

    onCommunityDocumentAttachment(): void {
        if (Utils.notNullOrEmpty(this.communityId)) {
            this.propertyCommunityDocuments.show(
                this.attachCommunity,
                this.communityId,
                null,
                0,
                this.communityDocuments
            );
        }
    }

    onOwnerDocumentAttachment(): void {
        if (Utils.notNullOrEmpty(this.communityId) && Utils.notNullOrEmpty(this.customerId)) {
            this.propertyCommunityDocuments.show(
                this.attachOwner,
                this.communityId,
                this.customerId,
                0,
                this.ownerDocuments
            );
        }
    }

    onAttachDocuments(attachments: any[]): void {
        attachments.forEach((document) => {
            if (Utils.isFalse(this.isAttachmentExists(document))) {
                document.action = ActionConstants.ADD;
                if (Utils.isTrue(this.multipleSelect)) {
                    this.selectedAttachments.push(document);
                } else {
                    this.selectedAttachments = [document];
                }
                this.updateAttachments(this.selectedAttachments);
            } else {
                this.messageBox.showErrorMessage('Error', 'The selected file already exist please select another file');
            }
        });
    }

    private updateAttachments(attachments: any[]): void {
        this.onAttachmentUpdate.emit(attachments);
    }

    private isAttachmentExists(file: any): boolean {
        let fileExists = false;
        this.selectedAttachments.forEach((attachment) => {
            if (
                (Utils.notNullOrEmpty(file.fileId) &&
                    Utils.notNullOrEmpty(attachment.fileId) &&
                    Utils.equals(file.fileId, attachment.fileId)) ||
                (Utils.notNullOrEmpty(file.fileName) &&
                    Utils.notNullOrEmpty(attachment.fileName) &&
                    Utils.equalsIgnoreCase(file.fileName, attachment.fileName)) ||
                (Utils.notNullOrEmpty(file.name) &&
                    Utils.notNullOrEmpty(attachment.name) &&
                    Utils.equalsIgnoreCase(file.name, attachment.name)) ||
                (Utils.notNullOrEmpty(file.name) &&
                    Utils.notNullOrEmpty(attachment.fileName) &&
                    Utils.equalsIgnoreCase(file.name, attachment.fileName))
            ) {
                fileExists = true;
            }
        });

        return fileExists;
    }

    private downloadFile(file: any): void {
        if (Utils.isTrue(this.hasDownloadUrl(file))) {
            this.loadingVisible = true;
            this.helperService.downloadFileUsingUrl(
                file.fileUrl || file.downloadUrl,
                file.fileName || file.name,
                () => {
                    this.loadingVisible = false;
                }
            );
        }
    }

    private closeViewer(): void {
        this.visible = false;
        this.onClose.emit();
    }

    private processAttachments(selectedAttachments: any[]): void {
        if (Utils.notNullOrEmpty(selectedAttachments)) {
            selectedAttachments.forEach((attachment) => {
                if (Utils.notInList('allowDelete', Object.keys(attachment))) {
                    attachment.allowDelete = true;
                }
            });
        }
    }

    private removeAttachment(record: any): void {
        if (Utils.notEqualsIgnoreCase(this.selectedAttachments[record.rowIndex].action, ActionConstants.ADD)) {
            this.selectedAttachments[record.rowIndex].action = ActionConstants.DELETE;
        } else {
            this.selectedAttachments.splice(record.rowIndex, 1);
        }

        this.onDocumentDelete.emit(record.data);
        setTimeout(() => {
            if (DxWidgetUtils.hasInstance(this.attachmentGrid)) {
                this.attachmentGrid.instance.refresh();
            }
        }, 250);
    }
}
